import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { OrderListItemResponse, StoreResponse } from '@clarity/contract';
import { useOrders } from '../../queries/index.js';
import { useScopeContext } from '../../lib/scopeContext.js';
import { isNotYetOnboarded } from '../../lib/lifecycle.js';
import { useI18n } from '../../lib/i18n.js';
import { isOnboardingRequired } from '../format.js';
import { StoreOrders } from '../store/StoreOrders.js';
import {
  OrderEconomicsRow,
  OrdersRollupTable,
  OrdersTableHead,
  computeRollup,
} from '../orders/OrdersEconomicsTable.js';

/**
 * CombinedOrders (clarity-navigation-scope task 6.1, Req 6.1–6.4, design §7) —
 * the Orders flexible surface at an All / Brand scope that covers MORE than one
 * branch. It fans out the existing per-store {@link useOrders} hook over the
 * in-scope ONBOARDED branches, merges their rows client-side (each tagged with
 * its `branch`), and renders the existing orders list with an extra **Store**
 * column.
 *
 * React-safe fan-out: hooks can't be called in a loop whose length varies across
 * renders, so each branch gets its OWN child component ({@link BranchOrderSource})
 * that calls `useOrders(branch.id)` and REPORTS its result up via a callback. The
 * parent collects the per-branch results into a map keyed by branch id and merges
 * them for the table. This also gives per-branch loading/error ISOLATION (Req
 * 6.3): one branch failing or still loading only renders that branch's row as
 * errored/loading — the rest of the table renders normally.
 *
 * Opening an order routes to that order's branch store scope + `?order=` (Req
 * 6.1), so the UNCHANGED single-store order detail in {@link StoreOrders} handles
 * it — no duplicated detail view.
 *
 * These are cheap per-branch DB reads (not portal fetches; design §7 / Req 6.5)
 * fanned out in parallel. The query keys stay per-branch (`queryKeys.orders`), so
 * the cache is shared with the single-store orders view. For a handful of
 * branches this is fine; a future server-side aggregate would replace the
 * fan-out for a client with dozens of branches.
 *
 * Single branch (Req 6.4): when the scope resolves to exactly one branch, it
 * renders the standalone single-store Orders page ({@link StoreOrders}) — no
 * "all" chrome, no Store column. (The surface-level single-vs-combined routing is
 * wired in task 6.4; this component is self-contained so either path is correct.)
 */

/** The translator shape from {@link useI18n}, narrowed for the helpers below. */
type Translate = ReturnType<typeof useI18n>['t'];

/** A shared frozen empty-rows reference, so the no-data case is stable across renders. */
const EMPTY_ROWS: readonly OrderListItemResponse[] = Object.freeze([]);

/** The per-branch read state a {@link BranchOrderSource} reports up to the parent. */
interface BranchResult {
  isLoading: boolean;
  /** A genuine transport failure (NOT the expected `409 onboarding_required`). */
  isError: boolean;
  /** The list rows — each `{ order, economics }` (clarity-orders-detail Req 4.1). */
  rows: readonly OrderListItemResponse[];
}

export function CombinedOrders() {
  const { scope, branches, isSingleBranch } = useScopeContext();

  // Req 6.4 — a one-branch scope renders exactly today's single-store view. A
  // one-branch brand degenerates to its single branch; a store scope is always
  // one branch. Either way, no combined chrome.
  if (isSingleBranch && branches.length === 1) {
    return (
      <section data-surface="orders" data-scope={scope.kind}>
        <StoreOrders store={branches[0] as StoreResponse} />
      </section>
    );
  }

  return <CombinedOrdersTable />;
}

/**
 * The multi-branch combined table. Split from {@link CombinedOrders} so the
 * single-branch passthrough never mounts the fan-out machinery (and the hook
 * count stays stable: this component always renders the same number of
 * {@link BranchOrderSource} children for a given in-scope branch set).
 */
function CombinedOrdersTable() {
  const { scope, branches } = useScopeContext();
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();

  // Only ONBOARDED branches contribute data reads (Req 6.2): an un-onboarded
  // branch has no persisted orders and its read would be rejected `409`.
  const onboarded = useMemo(
    () => branches.filter((b) => !isNotYetOnboarded(b)),
    [branches],
  );
  const stillOnboarding = branches.length - onboarded.length;

  // Per-branch results, keyed by branch id. Each BranchOrderSource reports its
  // read state here; the merge below reads whatever has arrived so far, so a
  // slow/failed branch never blocks the rest (Req 6.3).
  const [results, setResults] = useState<Record<string, BranchResult>>({});

  const report = useCallback((branchId: string, result: BranchResult) => {
    setResults((prev) => {
      const existing = prev[branchId];
      // Avoid a needless state update (and re-render loop) when nothing changed.
      if (
        existing &&
        existing.isLoading === result.isLoading &&
        existing.isError === result.isError &&
        existing.rows === result.rows
      ) {
        return prev;
      }
      return { ...prev, [branchId]: result };
    });
  }, []);

  // Open an order in ITS branch's store scope (Req 6.1): the unchanged
  // single-store OrderDetail renders from `/store/:branchId/orders?order=:id`.
  const openOrder = useCallback(
    (branchId: string, orderId: string) => {
      if (!clientId) return;
      navigate(`/c/${clientId}/store/${branchId}/orders?order=${orderId}`);
    },
    [clientId, navigate],
  );

  // Merge the arrived rows across branches, tagging each with its branch so the
  // Store column + the open-order drill know which branch an order belongs to.
  // Each row carries its `{ order, economics }`; the shared table reads the
  // economics columns (commission / food cost / profit / net payout) off
  // `row.economics` (clarity-orders-detail task 5.1).
  const merged = useMemo(() => {
    const out: { branch: StoreResponse; row: OrderListItemResponse }[] = [];
    for (const branch of onboarded) {
      const result = results[branch.id];
      if (!result) continue;
      for (const row of result.rows) out.push({ branch, row });
    }
    // Newest first, so the combined list reads like a single stream across
    // branches rather than branch-by-branch.
    out.sort((a, b) => b.row.order.timestamp.localeCompare(a.row.order.timestamp));
    return out;
  }, [onboarded, results]);

  const anyLoading = onboarded.some((b) => (results[b.id]?.isLoading ?? true));
  const allSettled = onboarded.every((b) => results[b.id] && !results[b.id]!.isLoading);
  const erroredBranches = onboarded.filter((b) => results[b.id]?.isError);

  const scopeNoBranches = onboarded.length === 0;

  // The optional scope-level roll-up (Req 2.6): Σ commission / food cost /
  // profit / net payout across the merged rows (non-null figures only). The
  // shared helper sums the same `economics` the rows render, so the total and
  // the column figures agree.
  const rollup = useMemo(
    () => computeRollup(merged.map(({ row }) => row)),
    [merged],
  );

  return (
    <section data-surface="orders" data-scope={scope.kind} className="combined-orders">
      {/* The fan-out: one hidden source per onboarded branch. Rendering them as
          children keeps the hooks rules happy (fixed per-branch component) and
          isolates each branch's read. */}
      {onboarded.map((branch) => (
        <BranchOrderSource key={branch.id} branch={branch} onResult={report} />
      ))}

      {stillOnboarding > 0 && <OnboardingNote count={stillOnboarding} t={t} />}

      {scopeNoBranches ? (
        <p className="muted">
          {stillOnboarding > 0
            ? 'All in-scope branches are still onboarding — no data to combine yet.'
            : 'No branches in scope.'}
        </p>
      ) : (
        <>
          {/* The optional scope-level roll-up (Req 2.6) above the table. */}
          <OrdersRollupTable rollup={rollup} />

          <table className="data-table combined-orders__table" aria-label="Orders">
            <OrdersTableHead withStore />
            <tbody>
              {merged.map(({ branch, row: { order, economics } }) => (
                <OrderEconomicsRow
                  key={`${branch.id}:${order.id}`}
                  order={order}
                  economics={economics}
                  branch={branch}
                  onOpen={() => openOrder(branch.id, order.id)}
                />
              ))}
              {/* Per-branch loading/error rows, isolated (Req 6.3). A branch that
                  is still loading or has failed shows its own status row inside
                  the same table while the merged rows above render normally. */}
              {onboarded.map((branch) => {
                const result = results[branch.id];
                const loading = result?.isLoading ?? true;
                const errored = result?.isError ?? false;
                if (!loading && !errored) return null;
                return (
                  <tr key={`status:${branch.id}`} className="combined-orders__status-row">
                    <td>
                      <span className="combined-orders__store">
                        {branch.name ?? branch.externalStoreId}
                      </span>
                    </td>
                    <td colSpan={9}>
                      {errored ? (
                        <span role="alert" className="error">
                          Could not load orders for this branch.
                        </span>
                      ) : (
                        <span role="status" className="muted">
                          Loading orders…
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {allSettled && erroredBranches.length === 0 && merged.length === 0 && (
            <p className="muted">No orders yet across the in-scope branches.</p>
          )}
          {!anyLoading && erroredBranches.length > 0 && (
            <p className="muted combined-orders__partial" role="note">
              Showing {merged.length} order{merged.length === 1 ? '' : 's'} —{' '}
              {erroredBranches.length} branch
              {erroredBranches.length === 1 ? '' : 'es'} could not be loaded.
            </p>
          )}
        </>
      )}
    </section>
  );
}

/**
 * One per-branch read source (clarity-navigation-scope design §7). It calls the
 * existing {@link useOrders} hook for ONE branch and reports its state up via
 * `onResult`; it renders nothing itself. Rendering one of these per branch is the
 * React-safe way to fan a hook out over a variable-length branch set — the hook
 * count is fixed within each child, and a child unmounts/mounts cleanly as the
 * in-scope set changes.
 *
 * The expected `409 onboarding_required` is NOT treated as a failure — the parent
 * already filters to onboarded branches, but if a branch's read ever surfaces the
 * `409` (a just-flipped lifecycle, defense-in-depth), it reports empty rows and
 * no error rather than a scary error row.
 */
function BranchOrderSource({
  branch,
  onResult,
}: {
  branch: StoreResponse;
  onResult: (branchId: string, result: BranchResult) => void;
}) {
  const orders = useOrders(branch.id);
  const onboardingBlocked = isOnboardingRequired(orders.error);
  // A `409` is the expected not-yet-onboarded block, not a transport failure.
  const isError = orders.isError && !onboardingBlocked;
  const { isLoading } = orders;
  // The query body reference is stable across renders until the data changes, so
  // use it directly (a `[]` fallback literal would be a fresh array each render
  // and re-fire the effect) — a shared frozen EMPTY_ROWS covers the no-data case.
  const rows: readonly OrderListItemResponse[] =
    orders.data?.status === 200 && !onboardingBlocked ? orders.data.body : EMPTY_ROWS;

  // Report the latest state up in an EFFECT, not during render — a child must
  // not call a parent's setState while rendering (React forbids updating another
  // component mid-render). `onResult` is memoized and dedupes no-op updates, so
  // this settles after each query transition without looping.
  useEffect(() => {
    onResult(branch.id, { isLoading, isError, rows });
  }, [branch.id, isLoading, isError, rows, onResult]);

  return null;
}

/**
 * The "N still onboarding" note (Req 6.2): how many in-scope branches are
 * excluded from the combined reads because they are not yet onboarded. Uses the
 * bilingual `scope.combined.onboarding.*` keys (one/many) with `{count}`.
 */
function OnboardingNote({ count, t }: { count: number; t: Translate }) {
  const key =
    count === 1 ? 'scope.combined.onboarding.one' : 'scope.combined.onboarding.many';
  return (
    <p className="combined-orders__onboarding-note muted" role="note">
      {t(key, { count })}
    </p>
  );
}
